import { captureException } from "@sentry/react";
import { useAtom } from "jotai";

import { userRecordActivityAtom } from "../../../../../atoms/GlobalAtoms";
import API from "../../../../../api/api/API";

const MAX_RECORD_ACTIVITY_LIMIT = 25;

interface UserRecordActivityBasic {
  moduleAPIName: string;
  recordIDs: string[];
  type: "Favorites" | "RecentlyViewed";
}
interface UserRecordActivityRecent {
  moduleAPIName: string;
  add: string[];
  remove: string[];
  type: "Recent";
}

export type UserRecordActivityPayload =
  | UserRecordActivityBasic
  | UserRecordActivityRecent;

export function useUserRecordActivityUpdate() {
  const [userRecordActivity, setUserRecordActivity] = useAtom(
    userRecordActivityAtom
  );

  const updateUserRecordActivityCache = async ({
    userRecordActivities,
  }: {
    userRecordActivities: UserRecordActivityPayload[];
  }) => {
    // Clone the current state to avoid mutation
    let updatedUserRecordActivity = structuredClone(userRecordActivity || {});

    // Process each activity payload
    for (const activity of userRecordActivities) {
      const { moduleAPIName, type } = activity;

      if (!moduleAPIName) continue;

      // If this module hasn't been initialized yet, set the defaults
      if (!updatedUserRecordActivity[moduleAPIName]) {
        updatedUserRecordActivity[moduleAPIName] = {
          Favorites: [] as string[],
          RecentlyViewed: [] as string[],
          Recent: {}, // Stores the ID of the Record, and then how many times it has been used.
          Fields: [],
        };
      }

      switch (type) {
        case "Favorites": {
          const favRecordIDs = activity.recordIDs;
          const favorites = updatedUserRecordActivity[moduleAPIName].Favorites;

          for (const recordID of favRecordIDs) {
            const exists = favorites.includes(recordID);

            // If the record isn't already present and the list is full then skip
            if (!exists && favorites.length === MAX_RECORD_ACTIVITY_LIMIT)
              continue;

            if (exists) {
              // Remove the record, user clicked favorite on an already favorited record (Toggled it)
              updatedUserRecordActivity[moduleAPIName].Favorites =
                favorites.filter((id) => id !== recordID);
            } else {
              // Add new record at the beginning
              favorites.unshift(recordID);
            }
          }
          break;
        }
        case "RecentlyViewed": {
          const recentRecordIDs = activity.recordIDs;
          let recentlyViewed =
            updatedUserRecordActivity[moduleAPIName].RecentlyViewed;

          for (const recordID of recentRecordIDs) {
            // Ensure record is at top, by filtering it out first
            recentlyViewed = recentlyViewed.filter((id) => id !== recordID);

            recentlyViewed.unshift(recordID);

            // Enforce maximum record length
            if (recentlyViewed.length > MAX_RECORD_ACTIVITY_LIMIT) {
              recentlyViewed.pop();
            }
          }

          // Storing the value because filter returns a new array reference.
          updatedUserRecordActivity[moduleAPIName].RecentlyViewed =
            recentlyViewed;
          break;
        }
        case "Recent": {
          const recentlyUsed = updatedUserRecordActivity[moduleAPIName].Recent;
          const recentlyUsedIDs = Object.keys(recentlyUsed);

          // Combine the two payload arrays
          const newRecentlyUsedIDs = activity.add.concat(activity.remove);

          for (const recordID of newRecentlyUsedIDs) {
            let currentNumber = recentlyUsed[recordID] || 0;

            if (currentNumber < 5 && !activity.remove.includes(recordID)) {
              currentNumber++;
            }

            if (activity.remove.includes(recordID)) {
              const updatedNumber = currentNumber - 1;
              recentlyUsed[recordID] = updatedNumber < 1 ? 1 : updatedNumber;
              continue;
            }

            // Ensure we don't exceed the maximum number of recommended records
            if (
              recentlyUsedIDs.length === MAX_RECORD_ACTIVITY_LIMIT &&
              !recentlyUsedIDs.includes(recordID)
            ) {
              const recordIDToRemove = recentlyUsedIDs.reduce(
                (prevKey, currentKey) => {
                  return recentlyUsed[currentKey] < recentlyUsed[prevKey]
                    ? currentKey
                    : prevKey;
                }
              );
              delete recentlyUsed[recordIDToRemove];
            }
            recentlyUsed[recordID] = currentNumber;
          }
          break;
        }
      }
    }

    // Update local state
    setUserRecordActivity(updatedUserRecordActivity);

    try {
      await API.updateUserRecordActivity(updatedUserRecordActivity);
    } catch (error) {
      await captureException(error);
    }
  };

  const updateUserModuleMetadata = async ({
    fields,
    moduleAPIName,
  }: {
    fields: string[];
    moduleAPIName: string;
  }) => {
    // Clone the current state to avoid mutation
    let updatedUserRecordActivity = structuredClone(userRecordActivity || {});

    let moduleActivity = updatedUserRecordActivity[moduleAPIName];

    if (!moduleActivity) {
      moduleActivity = {
        Favorites: [],
        Recent: {},
        RecentlyViewed: [],
        Fields: [],
      };
    }

    moduleActivity.Fields = fields;

    setUserRecordActivity(updatedUserRecordActivity);

    // Persist the changes to the API
    try {
      await API.updateUserRecordActivity(updatedUserRecordActivity);
    } catch (error) {
      console.error("Error updating user record activity on API:", error);
    }
  };

  return {
    updateUserRecordActivityCache,
    updateUserModuleMetadata,
    setUserRecordActivity,
    userRecordActivity,
  };
}
